/**
 * Billing Page for trAInedup.ai
 * Subscription management and billing interface
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import {
  CreditCard,
  DollarSign,
  Download,
  Settings,
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowUpCircle,
  ArrowDownCircle,
  X,
} from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { SUBSCRIPTION_TIERS, type SubscriptionTier } from '../services/stripe/config';
import { SUBSCRIPTION_PRICES, TIER_FEATURES, ROUTES } from '../utils/constants';
import { getPriceIdFromTier, previewUpgrade, scheduleDowngrade, createUpgradeCheckoutSession, confirmUpgradeCheckoutSession } from '../services/stripe/subscriptions';
import { functions } from '../services/firebase/config';

/**
 * Billing page component
 * Displays subscription status, billing history, and management options
 */
export const BillingPage: React.FC = () => {
  const {
    subscription,
    loading,
    error,
    isSubscriptionActive,
    isTrialing,
    isCanceled,
    getCurrentTier,
    getDaysUntilRenewal,
    getTrialDaysRemaining,
    cancelPlan,
    reactivatePlan,
    openCustomerPortal,
    refreshSubscription,
    subscribe,
  pendingDowngradeTier,
  pendingDowngradeEffectiveDate,
  cancelScheduledDowngrade,
  } = useSubscription();

  const { user } = useAuth();
  const navigate = useNavigate();

  const [showSuccess, setShowSuccess] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const confirmOnceRef = useRef(false);

  // Confirm modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTier, setConfirmTier] = useState<string | null>(null);
  const [confirmMode, setConfirmMode] = useState<'checkout' | 'update'>('checkout');
  const [confirmCurrency, setConfirmCurrency] = useState<string>('USD');
  const [confirmPriceDisplay, setConfirmPriceDisplay] = useState<string>('');
  const [previewAmountDue, setPreviewAmountDue] = useState<number | null>(null);
  const [previewLines, setPreviewLines] = useState<Array<{ amount: number; currency: string; description?: string; proration: boolean }>>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const currencySymbol = SUBSCRIPTION_PRICES.standard.display.charAt(0);

  const formatMoney = (amountMinor: number | null, currency: string) => {
    if (amountMinor === null || amountMinor === undefined) return '—';
    const major = (amountMinor / 100).toFixed(2);
    const upper = currency.toUpperCase();
    const sym = upper === 'GBP' ? '£' : upper === 'EUR' ? '€' : '$';
    return `${sym}${major}`;
  };

  // Check for successful checkout (run once; avoid re-calling on every render)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const upgradeSessionId = params.get('upgrade_session_id');
    if ((sessionId || upgradeSessionId) && !confirmOnceRef.current) {
      confirmOnceRef.current = true;
      setShowSuccess(true);
      (async () => {
        try {
          if (upgradeSessionId) {
            await confirmUpgradeCheckoutSession(upgradeSessionId);
          } else if (sessionId) {
            const mod = await import('../services/stripe/subscriptions');
            await mod.confirmCheckoutSession(sessionId);
          }
        } catch (e) {
          console.warn('confirm session failed (will rely on webhook):', e);
        } finally {
          refreshSubscription();
        }
      })();
      // Clear the URL parameter (update address bar)
      window.history.replaceState({}, '', '/dashboard/billing');
      // Hide success message after 5 seconds
      setTimeout(() => setShowSuccess(false), 5000);
    }
  }, [refreshSubscription]);

  // After success, poll briefly to wait for webhook write
  useEffect(() => {
    if (!showSuccess) return;
    let attempts = 0;
    const maxAttempts = 10; // ~20s
    const interval = setInterval(async () => {
      attempts += 1;
      try {
        await refreshSubscription();
      } catch (_) {}
      if (isSubscriptionActive() || attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [showSuccess, refreshSubscription, isSubscriptionActive]);

  /**
   * Handle plan change
   */
  const handlePlanChange = async (newTier: string) => {
    try {
      const isFree = getCurrentTier() === SUBSCRIPTION_TIERS.FREE;
      const isDowngrade = orderedTiers.indexOf(newTier as any) < orderedTiers.indexOf(currentTier as any);
      const details = planPricing[newTier as keyof typeof planPricing];
      const priceDisplay = `${currencySymbol}${details.price}/month`;

      if (isFree) {
        // Free -> Paid: keep Checkout, but confirm first
        setConfirmTier(newTier);
        setConfirmMode('checkout');
        setConfirmCurrency(currencySymbol === '£' ? 'GBP' : 'USD');
        setConfirmPriceDisplay(priceDisplay);
        setPreviewAmountDue(null);
        setPreviewLines([]);
        setConfirmOpen(true);
        return;
      }

      if (isDowngrade) {
        // Downgrades only at next billing cycle
        setConfirmTier(newTier);
        setConfirmMode('update');
        setConfirmCurrency(currencySymbol === '£' ? 'GBP' : 'USD');
        setConfirmPriceDisplay(priceDisplay);
        setPreviewAmountDue(0);
        setPreviewLines([]);
        setConfirmOpen(true);
        return;
      }

      // Upgrade: preview proration immediate total only
      setConfirmTier(newTier);
      setConfirmMode('update');
      setConfirmCurrency(currencySymbol === '£' ? 'GBP' : 'USD');
      setConfirmPriceDisplay(priceDisplay);
      setPreviewAmountDue(null);
      setPreviewLines([]);
      setConfirmOpen(true);
      setPreviewLoading(true);
      try {
        const priceId = getPriceIdFromTier(newTier as any);
        const preview = await previewUpgrade(priceId);
        setConfirmCurrency(preview.currency);
        setPreviewAmountDue(preview.amountDue);
        setPreviewLines(preview.lines || []);
      } catch (e) {
        console.warn('[Billing] previewUpgrade failed, will show generic confirm:', e);
      } finally {
        setPreviewLoading(false);
      }
    } catch (error) {
      console.error('Error changing plan:', error);
      setActionLoading(null);
    }
  };

  const onConfirmChange = async () => {
    if (!confirmTier) return;
    try {
      setActionLoading(`change-${confirmTier}`);
      const isDowngrade = orderedTiers.indexOf(confirmTier as any) < orderedTiers.indexOf(currentTier as any);
      if (confirmMode === 'checkout') {
        await subscribe(confirmTier as any); // redirect
        return;
      }
      if (isDowngrade) {
        const priceId = getPriceIdFromTier(confirmTier as any);
        const { effectiveDate } = await scheduleDowngrade(priceId, confirmTier as any);
        alert(`Your plan will change to ${confirmTier} on ${new Date(effectiveDate * 1000).toLocaleDateString()}.`);
        await refreshSubscription();
      } else {
        // Upgrade: use Stripe Checkout to collect proration immediately
        const priceId = getPriceIdFromTier(confirmTier as any);
        const { url } = await createUpgradeCheckoutSession(priceId, confirmTier as any);
        if (url) {
          window.location.href = url; // redirect to Stripe Checkout
          return;
        }
        // No payment required path: just refresh
        await refreshSubscription();
      }
    } catch (e) {
      console.error('Confirm change failed:', e);
    } finally {
      setConfirmOpen(false);
      setActionLoading(null);
    }
  };

  const onCancelChange = () => {
    setConfirmOpen(false);
    setActionLoading(null);
  };

  /**
   * Handle subscription cancellation
   */
  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.')) {
      return;
    }

    try {
      setActionLoading('cancel');
      await cancelPlan(true);
    } catch (error) {
      console.error('Error canceling subscription:', error);
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Handle subscription reactivation
   */
  const handleReactivateSubscription = async () => {
    try {
      setActionLoading('reactivate');
      // If fully canceled, skip API and start a new checkout
      if (subscription?.status === 'canceled') {
        await subscribe(getCurrentTier());
        return;
      }
      await reactivatePlan();
    } catch (err: any) {
      // If backend indicates fully canceled, start a new checkout automatically
      const msg = String((err && err.message) ? err.message : '');
      if (/fully canceled|subscribe again/i.test(msg)) {
        try {
          await subscribe(getCurrentTier());
          return;
        } catch (e) {
          console.error('Fallback subscribe after failed reactivate error:', e);
        }
      } else {
        console.error('Error reactivating subscription:', err);
      }
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Handle adding another year to 12-month subscription
   */
  const handleAddAnotherYear = async () => {
    try {
      setActionLoading('extend');
      const extendSubscription = httpsCallable(functions, 'extendTwelveMonthSubscription');
      const result = await extendSubscription({ priceId: SUBSCRIPTION_PRICES.twelve_month_package.stripePriceId });
      const data = result.data as { success: boolean; checkoutUrl: string };
      
      if (data.success && data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
    } catch (error: any) {
      console.error('Error extending subscription:', error);
      alert(error.message || 'Failed to create checkout session');
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Handle switching to monthly subscription after 12-month package
   * Creates subscription that starts when 12-month expires (stacks)
   */
  const handleSwitchToMonthly = async () => {
    if (!twelveMonthExpiryDate) {
      alert('Unable to determine expiry date');
      return;
    }

    const expiryDateStr = twelveMonthExpiryDate.toLocaleDateString('en-GB');
    if (!window.confirm(
      `Switch to monthly subscription?\n\n` +
      `Your monthly subscription will start on ${expiryDateStr} when your 12-month package expires.\n` +
      `You can purchase this now and it will automatically activate on your expiry date.`
    )) {
      return;
    }

    // Navigate to pricing page where they can choose standard/premium monthly
    navigate('/pricing?from=twelve-month');
  };

  /**
   * Handle customer portal access
   */
  const handleOpenCustomerPortal = async () => {
    try {
      setActionLoading('portal');
      await openCustomerPortal();
    } catch (error) {
      console.error('Error opening customer portal:', error);
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Get subscription status badge
   */
  const getStatusBadge = () => {
    // Check if user has twelve_month_package (non-subscription tier)
    if (user?.subscriptionTier === SUBSCRIPTION_TIERS.TWELVE_MONTH_PACKAGE) {
      const endDate = user?.subscriptionEndDate;
      const now = new Date();
      const isExpired = endDate && endDate.toDate() < now;
      
      if (isExpired) {
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <AlertCircle className="w-4 h-4 mr-1" />
            Expired
          </span>
        );
      }
      
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-4 h-4 mr-1" />
          Active
        </span>
      );
    }
    
    if (!subscription) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
          Free Plan
        </span>
      );
    }

    if (isTrialing()) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
          <Clock className="w-4 h-4 mr-1" />
          Trial
        </span>
      );
    }

    if (isCanceled()) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
          <X className="w-4 h-4 mr-1" />
          Canceled
        </span>
      );
    }

    if (isSubscriptionActive()) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-4 h-4 mr-1" />
          Active
        </span>
      );
    }

    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
        <AlertCircle className="w-4 h-4 mr-1" />
        {subscription.status}
      </span>
    );
  };

  /**
   * Get plan pricing information
   */
  const getPlanPricing = () => {
    const priceFromDisplay = (display: string) => Number((display || '').replace(/[^0-9.]/g, '')) || 0;
    return {
      [SUBSCRIPTION_TIERS.FREE]: { price: 0, features: ['Basic AI Introduction', 'Limited Access'] },
      [SUBSCRIPTION_TIERS.STANDARD]: { price: priceFromDisplay(SUBSCRIPTION_PRICES.standard.display), features: [...TIER_FEATURES.standard] },
      [SUBSCRIPTION_TIERS.PREMIUM]: { price: priceFromDisplay(SUBSCRIPTION_PRICES.premium.display), features: [...TIER_FEATURES.premium] },
      [SUBSCRIPTION_TIERS.TWELVE_MONTH_PACKAGE]: { price: priceFromDisplay(SUBSCRIPTION_PRICES.twelve_month_package.display), features: [...TIER_FEATURES.twelve_month_package] },
      [SUBSCRIPTION_TIERS.STACKCOMMERCE_LIFETIME]: { price: 0, features: [...TIER_FEATURES.standard, 'Lifetime membership via Stackcommerce activation'] },
      // Corporate has no Stripe price; keep features for display but no price
      [SUBSCRIPTION_TIERS.CORPORATE]: { price: 0, features: [...TIER_FEATURES.corporate] },
      [SUBSCRIPTION_TIERS.ANNUAL]: { price: 0, features: ['Annual billing plan'] },
    } as const;
  };

  // Get current tier and plan pricing after helper functions are defined
  const planPricing = getPlanPricing();
  const currentTier = getCurrentTier();
  
  // Check if user is on 12-month package
  const isTwelveMonthUser = currentTier === SUBSCRIPTION_TIERS.TWELVE_MONTH_PACKAGE;
  const isStackCommerceUser = currentTier === SUBSCRIPTION_TIERS.STACKCOMMERCE_LIFETIME;
  const twelveMonthExpiryDate = user?.subscriptionEndDate ? new Date(user.subscriptionEndDate.toDate()) : null;
  const daysUntilExpiry = twelveMonthExpiryDate ? Math.ceil((twelveMonthExpiryDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 0;

  // Only Standard and Premium are Stripe purchasable; Corporate is contact-only and shown last
  // 12-month package should only be visible to users who already have it
  // StackCommerce is a one-time external purchase and should never be shown
  // StackCommerce users (lifetime standard equivalent) should only see Premium and Corporate upgrade options
  const orderedTiers: readonly SubscriptionTier[] = (() => {
    // StackCommerce users only see upgrade options
    if (isStackCommerceUser) {
      return [
        SUBSCRIPTION_TIERS.PREMIUM,
        SUBSCRIPTION_TIERS.CORPORATE,
      ];
    }
    
    const baseTiers: SubscriptionTier[] = [
      SUBSCRIPTION_TIERS.FREE,
      SUBSCRIPTION_TIERS.STANDARD,
      SUBSCRIPTION_TIERS.PREMIUM,
    ];
    
    // Add 12-month package only if user has it
    if (isTwelveMonthUser) baseTiers.push(SUBSCRIPTION_TIERS.TWELVE_MONTH_PACKAGE);
    
    // Corporate always shown last
    baseTiers.push(SUBSCRIPTION_TIERS.CORPORATE);
    
    return baseTiers;
  })();

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-8">
      {/* Success Message */}
      {showSuccess && (
        <Card className="bg-green-50 border-green-200">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <h3 className="font-medium text-green-800">Subscription Updated Successfully!</h3>
              <p className="text-sm text-green-600">
                Your subscription has been activated. You now have access to all premium features.
              </p>
            </div>
            <button
              onClick={() => setShowSuccess(false)}
              className="ml-auto text-green-400 hover:text-green-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </Card>
      )}

      {/* Error Message */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <div>
              <h3 className="font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Page Header */}
      <div className="flex flex-col gap-3 mb-6">
        <div className="flex flex-col gap-1">
          <h1 className="font-montserrat font-bold text-2xl text-neutral-800">
            Billing & Subscription
          </h1>
          <p className="text-neutral-600">
            Manage your subscription and billing information
          </p>
        </div>
        
        <Button
          variant="outline"
          icon={Settings}
          onClick={handleOpenCustomerPortal}
          loading={actionLoading === 'portal'}
          className="w-full sm:w-auto"
        >
          Manage Billing
        </Button>
      </div>

      {/* Current Subscription */}
      <Card>
        {pendingDowngradeTier && pendingDowngradeEffectiveDate && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded">
            <div className="flex items-center justify-between">
              <div className="text-sm text-amber-800">
                Pending change: your plan will switch to <span className="font-semibold capitalize">{pendingDowngradeTier}</span> on {pendingDowngradeEffectiveDate.toLocaleDateString()}.
              </div>
              <Button size="sm" variant="outline" onClick={async () => { try { setActionLoading('cancel-downgrade'); await (cancelScheduledDowngrade?.()); await refreshSubscription(); } finally { setActionLoading(null); } }} loading={actionLoading === 'cancel-downgrade'}>
                Cancel change
              </Button>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-montserrat font-semibold text-xl text-neutral-800">
            Current Subscription
          </h2>
          {getStatusBadge()}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Plan Details */}
          <div>
            <h3 className="font-medium text-neutral-800 mb-2">Plan</h3>
            <p className="text-2xl font-bold text-primary capitalize">
              {currentTier} Plan
            </p>
            {subscription && planPricing[currentTier] && (
              <p className="text-neutral-600">
                {currencySymbol}{planPricing[currentTier].price}/month
              </p>
            )}
          </div>

          {/* Billing Period */}
          <div>
            <h3 className="font-medium text-neutral-800 mb-2">Billing Period</h3>
            {isTwelveMonthUser && twelveMonthExpiryDate ? (
              <div>
                <p className="text-neutral-800">
                  12-month access
                </p>
                <p className="text-sm text-neutral-600">
                  {daysUntilExpiry > 0 
                    ? `${daysUntilExpiry} days remaining`
                    : 'Expired'
                  }
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  Expires {twelveMonthExpiryDate.toLocaleDateString()}
                </p>
              </div>
            ) : subscription ? (
              <div>
                <p className="text-neutral-800">
                  {subscription.currentPeriodStart.toLocaleDateString()} - {subscription.currentPeriodEnd.toLocaleDateString()}
                </p>
                <p className="text-sm text-neutral-600">
                  {isTrialing() 
                    ? `${getTrialDaysRemaining()} days left in trial`
                    : `Renews in ${getDaysUntilRenewal()} days`
                  }
                </p>
              </div>
            ) : (
              <p className="text-neutral-600">No active subscription</p>
            )}
          </div>

          {/* Next Payment */}
          <div>
            <h3 className="font-medium text-neutral-800 mb-2">Next Payment</h3>
            {isTwelveMonthUser ? (
              <p className="text-neutral-600">No payment scheduled</p>
            ) : subscription && isSubscriptionActive() && !isCanceled() ? (
              (() => {
                const hasPending = !!pendingDowngradeTier && !!pendingDowngradeEffectiveDate;
                const amount = hasPending ? (planPricing[pendingDowngradeTier as keyof typeof planPricing]?.price || 0) : (planPricing[currentTier]?.price || 0);
                const dueDate = hasPending ? pendingDowngradeEffectiveDate! : subscription.currentPeriodEnd;
                return (
                  <div>
                    <p className="text-neutral-800">
                      {currencySymbol}{amount}
                    </p>
                    <p className="text-sm text-neutral-600">
                      Due {dueDate.toLocaleDateString()}
                    </p>
                    {hasPending && (
                      <p className="text-xs text-neutral-500 mt-1">After switch to {String(pendingDowngradeTier)}.</p>
                    )}
                  </div>
                );
              })()
            ) : (
              <p className="text-neutral-600">
                {isCanceled() ? 'Subscription canceled' : 'No payment scheduled'}
              </p>
            )}
          </div>

        </div>

        {/* Trial Warning */}
        {isTrialing() && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <Clock className="w-5 h-5 text-blue-600" />
              <div>
                <h4 className="font-medium text-blue-800">Trial Period Active</h4>
                <p className="text-sm text-blue-600">
                  Your trial ends in {getTrialDaysRemaining()} days. Add a payment method to continue your subscription.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Cancellation Notice */}
        {isCanceled() && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <div>
                  <h4 className="font-medium text-red-800">Subscription Canceled</h4>
                  <p className="text-sm text-red-600">
                    Your subscription will end on {subscription?.currentPeriodEnd.toLocaleDateString()}. 
                    You'll lose access to premium features after this date.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleReactivateSubscription}
                loading={actionLoading === 'reactivate'}
              >
                Reactivate
              </Button>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-6 flex flex-wrap gap-3">
          {isTwelveMonthUser ? (
            // 12-Month Package: Special actions
            <>
              <Button
                variant="primary"
                onClick={handleAddAnotherYear}
                loading={actionLoading === 'extend'}
              >
                Add Another Year ({SUBSCRIPTION_PRICES.twelve_month_package.display})
              </Button>
              
              <Button
                variant="outline"
                onClick={handleSwitchToMonthly}
                loading={actionLoading === 'switch-monthly'}
              >
                Switch to Monthly
              </Button>
              
              <Button
                variant="outline"
                icon={Download}
                onClick={handleOpenCustomerPortal}
                loading={actionLoading === 'portal'}
              >
                Download Invoices
              </Button>

              {/* Info about monthly switching */}
              <div className="w-full mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Monthly subscriptions will stack and begin when your 12-month package expires 
                  {twelveMonthExpiryDate && ` (${twelveMonthExpiryDate.toLocaleDateString()}, ${daysUntilExpiry} days from now)`}.
                </p>
              </div>
            </>
          ) : (
            // Regular subscriptions: Standard actions
            <>
              {subscription && isSubscriptionActive() && !isCanceled() && (
                <Button
                  variant="outline"
                  onClick={handleCancelSubscription}
                  loading={actionLoading === 'cancel'}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  Cancel Subscription
                </Button>
              )}
              
              <Button
                variant="outline"
                icon={Download}
                onClick={handleOpenCustomerPortal}
                loading={actionLoading === 'portal'}
              >
                Download Invoices
              </Button>
            </>
          )}
        </div>
      </Card>

      {/* Plan Comparison */}
      <Card>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-montserrat font-semibold text-xl text-neutral-800">
            Available Plans
          </h2>
          {isTwelveMonthUser && (
            <div className="flex items-center gap-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-lg">
              <span className="text-sm font-medium text-neutral-700">Credits:</span>
              <span className="text-lg font-bold text-primary-600">{user?.credits || 0}</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {orderedTiers.map((tier) => {
            const details = planPricing[tier];
            const isCurrent = currentTier === tier;
            const isUpgrade = orderedTiers.indexOf(tier) > orderedTiers.indexOf(currentTier as any);
            const isLifetimeTier = tier === SUBSCRIPTION_TIERS.STACKCOMMERCE_LIFETIME;
            return (
              <div
                key={tier}
                className={`border rounded-lg p-6 ${
                  isCurrent 
                    ? 'border-primary bg-primary-50' 
                    : 'border-neutral-200 hover:border-primary-300'
                }`}
              >
                <div className="text-center mb-4">
                  <h3 className="font-semibold text-lg text-neutral-800 capitalize">
                    {tier === SUBSCRIPTION_TIERS.TWELVE_MONTH_PACKAGE ? '12-Month Package' : tier}
                  </h3>
                  <div className="mt-2">
                    {tier === SUBSCRIPTION_TIERS.CORPORATE ? (
                      <span className="text-3xl font-bold text-neutral-800">
                        POA
                      </span>
                    ) : isLifetimeTier ? (
                      <span className="text-3xl font-bold text-neutral-800">Lifetime</span>
                    ) : tier === SUBSCRIPTION_TIERS.TWELVE_MONTH_PACKAGE ? (
                      <>
                        <span className="text-3xl font-bold text-neutral-800">
                          {SUBSCRIPTION_PRICES.twelve_month_package.display}
                        </span>
                        <span className="text-neutral-600"> one-time</span>
                      </>
                    ) : (
                      <>
                        <span className="text-3xl font-bold text-neutral-800">
                          {SUBSCRIPTION_PRICES.standard.display.startsWith('£') ? '£' : '$'}{details.price}
                        </span>
                        <span className="text-neutral-600">/month</span>
                      </>
                    )}
                  </div>
                </div>

                <ul className="space-y-2 mb-6">
                  {details.features.map((feature: string, index: number) => (
                    <li key={index} className="flex items-center text-sm text-neutral-600">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="space-y-2">
                  {isCurrent ? (
                    <Button variant="primary" fullWidth disabled>
                      Current Plan
                    </Button>
                  ) : tier === SUBSCRIPTION_TIERS.CORPORATE ? (
                    <Button
                      variant="accent"
                      fullWidth
                      onClick={() => navigate(ROUTES.corporateRequest)}
                    >
                      Request Corporate Package
                    </Button>
                  ) : tier === SUBSCRIPTION_TIERS.TWELVE_MONTH_PACKAGE ? (
                    <Button
                      variant="primary"
                      fullWidth
                      onClick={() => navigate('/twelve-month-package')}
                    >
                      Get 12-Month Package
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant={currentTier === SUBSCRIPTION_TIERS.FREE ? 'primary' : 'outline'}
                        fullWidth
                        onClick={() => handlePlanChange(tier)}
                        loading={actionLoading === `change-${tier}`}
                        icon={currentTier === SUBSCRIPTION_TIERS.FREE ? undefined : 
                              (isUpgrade ? ArrowUpCircle : ArrowDownCircle)}
                      >
                        {currentTier === SUBSCRIPTION_TIERS.FREE 
                          ? 'Subscribe' 
                          : isUpgrade 
                            ? 'Upgrade' 
                            : 'Downgrade'
                        }
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Confirm Modal */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-neutral-800 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-primary" />
                Confirm {confirmMode === 'checkout' ? 'Subscription' : 'Plan Change'}
              </h3>
            </div>

            {(() => {
              const isDowngrade = confirmTier && (orderedTiers.indexOf(confirmTier as any) < orderedTiers.indexOf(currentTier as any));
              if (confirmMode === 'checkout') {
                return (
                  <div className="space-y-2 text-neutral-700">
                    <p>Subscribe to the <span className="font-semibold capitalize">{confirmTier}</span> plan.</p>
                    <p>Price: <span className="font-semibold">{confirmPriceDisplay}</span> (plus taxes).</p>
                    <p className="text-sm text-neutral-500">You will be redirected to Stripe Checkout.</p>
                  </div>
                );
              }
              if (isDowngrade) {
                return (
                  <div className="space-y-2 text-neutral-700">
                    <p>Downgrade to <span className="font-semibold capitalize">{confirmTier}</span>.</p>
                    <p className="text-sm text-neutral-600">This change will take effect at your next billing cycle. You will continue on your current plan until then.</p>
                    <div className="p-3 bg-neutral-50 border border-neutral-200 rounded text-sm">
                      <p>Next charge on renewal (after the switch): <span className="font-semibold">{confirmPriceDisplay}</span>.</p>
                    </div>
                  </div>
                );
              }
              return (
                <div className="space-y-3 text-neutral-700">
                  <p>Change your plan to <span className="font-semibold capitalize">{confirmTier}</span>.</p>
                  <div className="p-3 bg-neutral-50 border border-neutral-200 rounded">
                    {previewLoading ? (
                      <p className="text-sm text-neutral-600">Calculating your immediate charge…</p>
                    ) : (
                      <>
                        <p className="text-sm">Immediate charge: <span className="font-semibold">{formatMoney(previewAmountDue ?? 0, confirmCurrency)}</span></p>
                        {previewLines?.length > 0 && (
                          <ul className="mt-2 space-y-1 max-h-28 overflow-auto text-sm">
                            {previewLines.map((l, i) => (
                              <li key={i} className="flex justify-between">
                                <span className="truncate mr-2">{l.description || 'Proration'}</span>
                                <span className="font-medium">{formatMoney(l.amount, l.currency)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        <p className="text-xs text-neutral-500 mt-1">Taxes may apply.</p>
                      </>
                    )}
                  </div>
                </div>
              );
            })()}

            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={onCancelChange}>Cancel</Button>
              <Button onClick={onConfirmChange} loading={actionLoading === `change-${confirmTier}`}>Confirm</Button>
            </div>
          </div>
        </div>
      )}

      {/* Billing History */}
      <Card>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-montserrat font-semibold text-xl text-neutral-800">
            Billing History
          </h2>
          <Button
            variant="outline"
            size="sm"
            icon={Download}
            onClick={handleOpenCustomerPortal}
          >
            View All
          </Button>
        </div>

        {subscription ? (
          <div className="space-y-4">
            {/* Mock billing history - replace with real data */}
            <div className="flex items-center justify-between p-4 border border-neutral-200 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-neutral-800">
                    {currentTier.charAt(0).toUpperCase() + currentTier.slice(1)} Plan
                  </p>
                  <p className="text-sm text-neutral-600">
                    {subscription.currentPeriodStart.toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium text-neutral-800">
                  {currentTier === SUBSCRIPTION_TIERS.STACKCOMMERCE_LIFETIME
                    ? 'Lifetime access'
                    : `${currencySymbol}${planPricing[currentTier as keyof typeof planPricing]?.price || 0}`}
                </p>
                <p className="text-sm text-green-600">
                  {currentTier === SUBSCRIPTION_TIERS.STACKCOMMERCE_LIFETIME ? 'Activation code' : 'Paid'}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-neutral-500">
            <CreditCard className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
            <p>No billing history available</p>
            <p className="text-sm">Subscribe to a plan to see your billing history</p>
          </div>
        )}
      </Card>
    </div>
  );
};
